'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { MicrocopyFragment } from '@graphql/generated-contentful/graphql';
import { useCallback, useMemo } from 'react';
import { TreeAccordion } from 'ui-components';
import { FacetAccordion } from '../FacetAccordion';
import { FacetDropdown } from '../FacetDropdown';
import { VERSION_RANGES, getUnityVersionController } from '../facetControllers';
import { NumericFlatFacetValue, useFlatFacet } from '../hooks/useFlatFacet';
import { UnityVersionFacetProps } from './UnityVersionFacet.types';

const getValueId = (v: NumericFlatFacetValue): string =>
  `unity-version-${v.start}-${v.end}`;

export const UnityVersionFacet = ({
  variant = 'horizontal',
  className,
  isOpen,
  onToggle,
}: UnityVersionFacetProps) => {
  const { searchLabelsMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyFragment[];
  }>();

  const versionLabel =
    searchLabelsMicrocopy?.find((m) => m.key === 'search.unity-version.title')
      ?.value ?? 'Unity Version';

  const clearLabel =
    searchLabelsMicrocopy?.find((m) => m.key === 'search.unity-version.clear')
      ?.value ?? 'Clear';

  const {
    optimisticValues,
    selectedIds,
    selectedCount,
    handleClear,
    handleSelectMany,
  } = useFlatFacet({
    getController: getUnityVersionController,
    fieldName: 'min_unity_version',
    getValueId,
  });

  const getLabel = useCallback(
    (start: number, end: number) => {
      const range = VERSION_RANGES.find(
        (r) => r.start === start && r.end === end
      );
      if (!range) return `${start}-${end}`;
      const entry = searchLabelsMicrocopy?.find((m) => m.key === range.key);
      return entry?.value ?? range.label;
    },
    [searchLabelsMicrocopy]
  );

  const isDark = variant === 'mobile';

  const treeItems = useMemo(
    () =>
      optimisticValues.map((v) => ({
        id: getValueId(v),
        label: getLabel(v.start, v.end),
        count: v.numberOfResults,
        disabled: v.numberOfResults === 0 && v.state !== 'selected',
      })),
    [optimisticValues, getLabel]
  );

  const content = (
    <TreeAccordion
      items={treeItems}
      selectionMode="multiple"
      selectedIds={selectedIds}
      onSelectMany={handleSelectMany}
      isDarkTheme={isDark}
      size={variant === 'sidebar' ? 'default' : 'sm'}
    />
  );

  if (variant === 'horizontal') {
    return (
      <FacetDropdown
        label={versionLabel}
        selectedCount={selectedCount}
        clearLabel={clearLabel}
        onClear={handleClear}
        className={className}
      >
        {content}
      </FacetDropdown>
    );
  }

  return (
    <FacetAccordion
      title={versionLabel}
      variant={variant}
      className={className}
      onClear={handleClear}
      clearLabel={clearLabel}
      showClear={variant === 'drawer' && selectedCount > 0}
      defaultOpen={variant !== 'drawer'}
      selectedCount={selectedCount}
      isOpen={isOpen}
      onToggle={onToggle}
    >
      {content}
    </FacetAccordion>
  );
};
